package er.prateek.crossly.config

import er.prateek.crossly.data.model.DifficultyTier
import er.prateek.crossly.game.crossword.KeyboardMode
import er.prateek.crossly.game.crossword.StarterAmount
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

/**
 * The `gameplay` section of [AppConfig] -- remotely-tunable assist limits, starting with
 * letter reveals. See that class for how it is fetched and parsed.
 */
@Serializable
data class GameplayConfig(
    @SerialName("reveal_letter") val revealLetter: RevealLetterConfig = RevealLetterConfig(),
    @SerialName("keyboard_options") val keyboardOptions: KeyboardOptionsConfig = KeyboardOptionsConfig(),
    @SerialName("starter_options") val starterOptions: StarterOptionsConfig = StarterOptionsConfig(),
) {
    companion object {
        val DEFAULT = GameplayConfig()
    }
}

@Serializable
data class RevealLetterConfig(
    /** Hard ceiling across every puzzle played in a UTC day. `0` means unlimited. */
    @SerialName("max_per_day") val maxPerDay: Int = 3,
    /** Per-puzzle ceiling, easier tiers capped tighter so assists stay meaningful. */
    @SerialName("max_per_puzzle_easy") val maxPerPuzzleEasy: Int = 1,
    @SerialName("max_per_puzzle_medium") val maxPerPuzzleMedium: Int = 2,
    @SerialName("max_per_puzzle_hard") val maxPerPuzzleHard: Int = 3,
    @SerialName("max_per_puzzle_expert") val maxPerPuzzleExpert: Int = 4,
    /**
     * Require a completed rewarded ad before the letter is revealed. Only takes effect when
     * a rewarded ad is actually available to offer -- see [er.prateek.crossly.ads.AdsCoordinator.isRewardedAvailable]
     * -- so ad-free entitlements and platforms without rewarded support still get the reveal,
     * just without the ad requirement.
     */
    @SerialName("require_ad") val requireAd: Boolean = true,
    /** How many stars a single letter reveal costs from the player's earned-star balance. */
    @SerialName("star_cost") val starCost: Int = 1,
    /**
     * "Reveal Puzzle" (the whole-grid reveal) is a much bigger assist than a single letter --
     * off by default, and only ever a lever an operator pulls deliberately.
     */
    @SerialName("show_reveal_puzzle_button") val showRevealPuzzleButton: Boolean = false,
) {
    fun maxPerPuzzle(tier: DifficultyTier): Int = when (tier) {
        DifficultyTier.EASY -> maxPerPuzzleEasy
        DifficultyTier.MEDIUM -> maxPerPuzzleMedium
        DifficultyTier.HARD -> maxPerPuzzleHard
        DifficultyTier.EXPERT -> maxPerPuzzleExpert
    }
}

/**
 * Which "Letter keys" options (Settings > How you play) are offered, and which one a player
 * starts on.
 *
 * All four modes always work if selected -- the `show_*` flags only control which ones the
 * Settings picker offers. That picker is one control for every level, so the flags are
 * top-level only.
 *
 * `default` is one of `ALL_26`, `STILL_NEEDED`, `THIS_WORD`, `THIS_PUZZLE` (see
 * [keyboardModeFromConfig]), and [tiers] can give a level its own (`easy`, `medium`, `hard`,
 * `expert`): `{"tiers": {"easy": {"default": "THIS_WORD"}}}`. Defaults only drive a level the
 * player has never picked for; see [er.prateek.crossly.ui.gameplay.GameplayState.syncWithConfig].
 * Builds that predate `default`/`tiers` ignore both, which keeps this backward compatible.
 */
@Serializable
data class KeyboardOptionsConfig(
    @SerialName("show_all_26") val showAll26: Boolean = true,
    @SerialName("show_still_needed") val showStillNeeded: Boolean = false,
    @SerialName("show_this_word") val showThisWord: Boolean = false,
    @SerialName("show_this_puzzle") val showThisPuzzle: Boolean = true,
    @SerialName("default") val default: String = "THIS_PUZZLE",
    @SerialName("tiers") val tiers: Map<String, TierDefault> = emptyMap(),
) {
    val offered: Set<KeyboardMode>
        get() = buildSet {
            if (showAll26) add(KeyboardMode.FULL)
            if (showStillNeeded) add(KeyboardMode.RELEVANT)
            if (showThisWord) add(KeyboardMode.WORD)
            if (showThisPuzzle) add(KeyboardMode.THIS_PUZZLE)
        }

    // An unrecognised tier default falls back to the top-level one, and an unrecognised
    // top-level one to the compiled default, so a console typo never leaves a level without
    // a usable default.
    fun defaultFor(tier: DifficultyTier): KeyboardMode =
        tiers[tier.configKey]?.default?.let(::keyboardModeFromConfig)
            ?: keyboardModeFromConfig(default)
            ?: KeyboardMode.THIS_PUZZLE
}

/**
 * Which "Starter letters" options (Settings > How you play) are offered, and which one a
 * player starts on. Same shape and rules as [KeyboardOptionsConfig], including per-level
 * [tiers]. `default` is one of `NONE`, `FEW`, `MANY` -- see [StarterAmount].
 */
@Serializable
data class StarterOptionsConfig(
    @SerialName("show_none") val showNone: Boolean = true,
    @SerialName("show_few") val showFew: Boolean = true,
    @SerialName("show_many") val showMany: Boolean = false,
    @SerialName("default") val default: String = "FEW",
    @SerialName("tiers") val tiers: Map<String, TierDefault> = emptyMap(),
) {
    val offered: Set<StarterAmount>
        get() = buildSet {
            if (showNone) add(StarterAmount.NONE)
            if (showFew) add(StarterAmount.FEW)
            if (showMany) add(StarterAmount.MANY)
        }

    fun defaultFor(tier: DifficultyTier): StarterAmount =
        tiers[tier.configKey]?.default?.let(::starterAmountFromConfig)
            ?: starterAmountFromConfig(default)
            ?: StarterAmount.FEW
}

/** One level's entry under `tiers`. A missing `default` inherits the section's top-level one. */
@Serializable
data class TierDefault(
    @SerialName("default") val default: String? = null,
)

/** The key a level goes by under `tiers` -- lowercase, like `max_per_puzzle_easy`. */
val DifficultyTier.configKey: String get() = name.lowercase()

/**
 * Maps a console name to a [KeyboardMode]. The console uses the names players see in
 * Settings; the enum's own names are accepted too so either spelling works. Case-insensitive.
 */
fun keyboardModeFromConfig(name: String): KeyboardMode? = when (name.trim().uppercase()) {
    "ALL_26", "FULL" -> KeyboardMode.FULL
    "STILL_NEEDED", "RELEVANT" -> KeyboardMode.RELEVANT
    "THIS_WORD", "WORD" -> KeyboardMode.WORD
    "THIS_PUZZLE" -> KeyboardMode.THIS_PUZZLE
    else -> null
}

fun starterAmountFromConfig(name: String): StarterAmount? =
    StarterAmount.entries.firstOrNull { it.name == name.trim().uppercase() }
